import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/api";
import StatusBadge from "@/components/StatusBadge";
import ProtectedImage from "@/components/ProtectedImage";
import { Button } from "@/components/ui/button";
import ResponsiveFormDialog from "@/components/ResponsiveFormDialog";
import { EmptyState, ErrorState, ListSkeleton, Refreshing } from "@/components/admin/AsyncView";
import ConfirmDialog from "@/components/admin/ConfirmDialog";
import { fmtCondition, fmtLong, fmtShort } from "@/components/admin/format";

const TABS = [
  { key: "pending", label: "Menunggu persetujuan", sortHint: "Urut: jadwal ambil paling awal", emptyKind: "zero", emptyHint: "Tidak ada booking menunggu persetujuan." },
  { key: "returns", label: "Pengembalian", sortHint: "Urut: pengajuan terlama", emptyKind: "zero", emptyHint: "Tidak ada permintaan pengembalian." },
  { key: "overdue", label: "Terlambat", sortHint: "Urut: jatuh tempo terlama", emptyKind: "zero", emptyHint: "Tidak ada keterlambatan." },
];

const byTime = (get) => (a, b) => new Date(get(a) || 0) - new Date(get(b) || 0);
const SORTS = {
  pending: byTime((b) => b.start_time),
  returns: byTime((b) => b.created_at),
  overdue: byTime((b) => b.end_time),
};

const itemSummary = (b) => (b.lines || []).map((l) => `${l.name} (${l.qty})`).join(", ");

export default function QueuePage() {
  const [params, setParams] = useSearchParams();
  const tab = TABS.some((t) => t.key === params.get("tab")) ? params.get("tab") : "pending";
  const selectedId = params.get("selected") || null;

  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectInvalid, setRejectInvalid] = useState(false);
  const [extendFor, setExtendFor] = useState(null);
  const [extendMin, setExtendMin] = useState(60);
  const [damagedFor, setDamagedFor] = useState(null);
  const [forceFor, setForceFor] = useState(null);
  const [isPhone, setIsPhone] = useState(() => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches);
  const listScrollRef = useRef(0);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const onChange = (e) => setIsPhone(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const load = useCallback(async (background = false) => {
    if (background) setRefreshing(true);
    else { setLoading(true); setError(null); }
    try {
      // Minta 201 baris per antrean: bila penuh, daftar dipotong 200 terbaru
      // dan diberi label cap (F7) - hitungan tab tetap dari stats.
      const [stats, pending, returns, overdue] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/bookings", { params: { status: "PENDING", limit: 201 } }),
        api.get("/admin/bookings", { params: { status: "RETURN_REQUESTED", limit: 201 } }),
        api.get("/admin/bookings", { params: { status: "OVERDUE", limit: 201 } }),
      ]);
      setSnapshot({ stats: stats.data, pending: pending.data, returns: returns.data, overdue: overdue.data });
      setError(null);
    } catch (e) {
      // Refresh latar gagal: data lama tetap tampil, hanya tandai basi.
      if (!background) setError(errMsg(e));
      else toast.error(`Tidak dapat memperbarui: ${errMsg(e)}. Menampilkan data terakhir.`);
    } finally {
      setLoading(false); setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const lists = useMemo(() => {
    if (!snapshot) return { pending: [], returns: [], overdue: [], capped: {} };
    const cut = (arr, sort) => ({
      rows: [...arr].sort(sort).slice(0, 200),
      capped: arr.length > 200,
    });
    const pending = cut(snapshot.pending, SORTS.pending);
    const returns = cut(snapshot.returns, SORTS.returns);
    const overdue = cut(snapshot.overdue, SORTS.overdue);
    return {
      pending: pending.rows, returns: returns.rows, overdue: overdue.rows,
      capped: { pending: pending.capped, returns: returns.capped, overdue: overdue.capped },
    };
  }, [snapshot]);

  const counts = snapshot?.stats
    ? { pending: snapshot.stats.pending || 0, returns: snapshot.stats.return_requests || 0, overdue: snapshot.stats.overdue || 0 }
    : { pending: lists.pending.length, returns: lists.returns.length, overdue: lists.overdue.length };
  const total = counts.pending + counts.returns + counts.overdue;

  const rows = lists[tab];
  const selected = rows.find((b) => String(b.id) === String(selectedId)) || null;

  // Satu setParams atomik per interaksi: dua setter berurutan dengan closure
  // params yang sama bisa saling menimpa (setter tidak mengantre).
  const pick = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: key !== "tab" });
  };
  // Satu region di ponsel: simpan posisi daftar, kembali pulihkan posisi.
  const selectRow = (b) => {
    listScrollRef.current = window.scrollY;
    pick("selected", b.id);
  };
  const closeDetail = () => {
    const y = listScrollRef.current;
    pick("selected", null);
    setTimeout(() => window.scrollTo(0, y), 60);
  };
  const pickTab = (key) => {
    const next = new URLSearchParams(params);
    next.set("tab", key);
    next.delete("selected");
    setParams(next);
  };

  const runAction = async (booking, fn, successMsg) => {
    setBusyId(`${booking.id}`);
    try {
      await fn();
      toast.success(successMsg);
      await load(true);
      // Rekam bisa berubah oleh admin lain: seleksi dipertahankan hanya bila masih ada.
    } catch (e) {
      toast.error(errMsg(e));
      await load(true);
    } finally {
      setBusyId(null);
    }
  };

  const submitReject = () => {
    const reason = rejectReason.trim();
    if (!reason) {
      setRejectInvalid(true);
      document.getElementById("reject-reason-input")?.focus();
      return;
    }
    const target = rejectFor;
    setRejectFor(null); setRejectReason(""); setRejectInvalid(false);
    runAction(target, () => api.post(`/admin/bookings/${target.id}/reject`, { reason }), "Booking ditolak");
  };

  const extendNewDue = extendFor ? new Date(new Date(extendFor.end_time).getTime() + extendMin * 60000) : null;

  if (loading) {
    return (
      <div className="space-y-5">
        <QueueHeader total={null} />
        <div className="flex gap-2 border-b border-white/10" aria-hidden>
          {[0, 1, 2].map((i) => <div key={i} className="h-10 w-40 rounded-t-lg bg-white/5" />)}
        </div>
        <ListSkeleton rows={4} />
      </div>
    );
  }

  if (error && !snapshot) {
    return (
      <div className="space-y-5">
        <QueueHeader total={null} />
        <ErrorState message={error} onRetry={() => load()} />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <QueueHeader total={total} refreshing={refreshing} onRefresh={() => load(true)} />

      <div role="tablist" aria-label="Antrean tindakan" className="grid grid-cols-3 gap-1 border-b border-white/10 sm:flex sm:overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={tab === t.key}
            data-testid={`tab-${t.key}`}
            onClick={() => pickTab(t.key)}
            className={`min-w-0 border-b-2 px-1 py-2.5 text-center text-[13px] font-medium transition-colors sm:shrink-0 sm:px-3 sm:text-sm ${
              tab === t.key ? "border-[#007AFF] text-white" : "border-transparent text-zinc-400 hover:text-white"
            }`}
          >
            {t.label}
            <span className={`ml-1.5 font-mono text-xs tabular-nums ${tab === t.key ? "text-zinc-300" : "text-zinc-500"}`}>
              {counts[t.key]}
            </span>
          </button>
        ))}
      </div>

      <div className="items-center justify-between gap-3 min-[1440px]:flex">
        <p className="text-xs text-zinc-500">
          {rows.length} booking &middot; {TABS.find((t) => t.key === tab).sortHint.toLowerCase()}
          {lists.capped?.[tab] && " - menampilkan 200 terbaru (antrean melebihi 200)"}
        </p>
      </div>

      {selected && isPhone && (
        <button data-testid="queue-back-to-list" onClick={closeDetail}
          className="flex min-h-[44px] items-center gap-2 text-sm text-zinc-300 hover:text-white">
          <ArrowLeft className="h-4 w-4" /> Kembali ke daftar
        </button>
      )}

      <div className="grid items-start gap-4 min-[1440px]:grid-cols-[minmax(0,1fr)_400px]">
        <div className={selected && isPhone ? "hidden" : ""}>
          {rows.length === 0 ? (
            <EmptyState kind="zero" title="Tidak ada antrean di sini" hint={TABS.find((t) => t.key === tab).emptyHint} />
          ) : (
            <ol data-testid={`${tab}-list`} className="divide-y divide-white/5 overflow-hidden rounded-2xl border border-white/10 bg-zinc-900">
              {rows.map((b) => {
                const active = String(b.id) === String(selectedId);
                return (
                  <li key={b.id}>
                    <button
                      data-testid={`queue-row-${b.code}`}
                      onClick={() => (active ? closeDetail() : selectRow(b))}
                      aria-expanded={active}
                      className={`block w-full min-w-0 px-4 py-3 text-left transition-colors min-h-[56px] ${
                        active ? "bg-white/10" : "hover:bg-white/5"
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        <span className="font-mono text-xs text-zinc-500">{b.code}</span>
                        {busyId === `${b.id}` && <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />}
                      </span>
                      <span className="mt-0.5 block break-words text-sm font-semibold text-zinc-100">{b.user_name || "-"}</span>
                      <span className="block break-words text-sm text-zinc-400">{itemSummary(b)}</span>
                      <span className="block break-words text-xs text-zinc-500">
                        <RowMeta tab={tab} booking={b} />
                      </span>
                      <span className="mt-1.5 block">
                        <StatusBadge status={tab === "pending" ? "PENDING" : tab === "returns" ? "RETURN_REQUESTED" : "OVERDUE"} />
                      </span>
                    </button>
                  </li>
                );
              })}
            </ol>
          )}
        </div>

        <DetailPanel
          booking={selected}
          tab={tab}
          busyId={busyId}
          onClose={closeDetail}
          onApprove={(b) => runAction(b, () => api.post(`/admin/bookings/${b.id}/approve`), "Booking disetujui, kode akses dibuat")}
          onReject={(b) => { setRejectFor(b); setRejectReason(""); setRejectInvalid(false); }}
          onAcceptReturn={(b) => runAction(b, () => api.post(`/admin/bookings/${b.id}/confirm-return`), "Pengembalian diterima")}
          onDamaged={(b) => setDamagedFor(b)}
          onExtend={(b) => { setExtendFor(b); setExtendMin(60); }}
          onForce={(b) => setForceFor(b)}
        />
      </div>

      {/* Tolak dengan alasan khusus - alasan wajib, tercatat di audit. Pakai
          primitif dialog rumah (focus trap + Escape + focus kembali bawaan). */}
      <ResponsiveFormDialog
        open={!!rejectFor}
        onOpenChange={(o) => { if (!o) { setRejectFor(null); setRejectReason(""); setRejectInvalid(false); } }}
        title={rejectFor ? `Tolak booking ${rejectFor.code}` : "Tolak booking"}
        description="Alasan ini dikirim ke peminjam. Keputusan tercatat di log audit."
      >
        {rejectFor && (
          <>
            <div className="space-y-2">
              <label htmlFor="reject-reason-input" className="text-sm font-medium text-zinc-200">Alasan penolakan</label>
              <textarea
                id="reject-reason-input"
                data-testid="reject-reason-input"
                rows={3}
                value={rejectReason}
                onChange={(e) => { setRejectReason(e.target.value); setRejectInvalid(false); }}
                placeholder="Contoh: Kamera dipakai kegiatan lain 27-29 Okt"
                aria-invalid={rejectInvalid}
                className={`min-h-24 w-full rounded-xl border bg-zinc-950 px-3 py-2.5 text-sm text-zinc-100 placeholder:text-zinc-600 ${
                  rejectInvalid ? "border-rose-500" : "border-white/10"
                }`}
              />
              {rejectInvalid && <p role="alert" className="text-xs text-rose-400">Isi alasan penolakan dulu.</p>}
            </div>
            <div className="mt-5 flex gap-2.5 justify-end">
              <Button
                data-testid={`reject-cancel-button`}
                variant="outline"
                onClick={() => { setRejectFor(null); setRejectReason(""); setRejectInvalid(false); }}
                className="h-11 rounded-full border-white/15 bg-transparent px-5 text-sm text-zinc-200 hover:bg-white/10"
              >
                Batal
              </Button>
              <Button
                data-testid={`reject-submit-button`}
                disabled={busyId === `${rejectFor.id}`}
                onClick={submitReject}
                className="h-11 rounded-full border border-rose-500/40 bg-transparent px-5 text-sm font-semibold text-rose-300 hover:bg-rose-500/10"
              >
                {busyId === `${rejectFor.id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : "Tolak booking"}
              </Button>
            </div>
          </>
        )}
      </ResponsiveFormDialog>

      {/* Perpanjang: tampilkan jam kembali yang baru. Primitif dialog rumah. */}
      <ResponsiveFormDialog
        open={!!extendFor}
        onOpenChange={(o) => { if (!o) setExtendFor(null); }}
        title={extendFor ? `Perpanjang ${extendFor.code}` : "Perpanjang"}
        description="Jadwal kembali baru dihitung dari jadwal semula dan langsung terlihat peminjam."
      >
        {extendFor && (
          <>
            <div className="space-y-2">
              <label htmlFor="extend-minutes" className="text-sm font-medium text-zinc-200">Durasi perpanjangan</label>
              <select
                id="extend-minutes"
                data-testid="extend-minutes-select"
                value={extendMin}
                onChange={(e) => setExtendMin(Number(e.target.value))}
                className="h-12 w-full rounded-xl border border-white/10 bg-zinc-950 px-3 text-sm"
              >
                <option value={60}>60 menit</option>
                <option value={180}>3 jam</option>
                <option value={1440}>1 hari</option>
              </select>
              {extendNewDue && (
                <p className="text-sm text-zinc-300">
                  Jadwal kembali baru: <span className="font-medium text-white">{fmtLong(extendNewDue.toISOString())}</span>
                </p>
              )}
            </div>
            <div className="mt-5 flex gap-2.5 justify-end">
              <Button variant="outline" onClick={() => setExtendFor(null)}
                className="h-11 rounded-full border-white/15 bg-transparent px-5 text-sm text-zinc-200 hover:bg-white/10">
                Batal
              </Button>
              <Button
                data-testid="extend-submit-button"
                disabled={busyId === `${extendFor.id}`}
                onClick={() => {
                  const target = extendFor;
                  setExtendFor(null);
                  runAction(target, () => api.post(`/admin/bookings/${target.id}/extend`, { minutes: extendMin }), "Booking diperpanjang");
                }}
                className="h-11 rounded-full bg-[#0069DB] px-5 text-sm font-semibold text-white hover:bg-[#0058B6]"
              >
                {busyId === `${extendFor.id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : "Perpanjang"}
              </Button>
            </div>
          </>
        )}
      </ResponsiveFormDialog>

      <ConfirmDialog
        open={!!damagedFor}
        onOpenChange={(o) => !o && setDamagedFor(null)}
        title={`Terima dengan catatan rusak`}
        description={damagedFor ? `${damagedFor.code} - ${damagedFor.user_name}. Tindakan ini menandai SEMUA unit pada booking ini sebagai maintenance dengan kondisi rusak. Bila hanya sebagian unit yang rusak, catat unitnya ke peminjam dan tindak lanjuti manual setelah konfirmasi.` : ""}
        confirmLabel="Ya, tandai rusak"
        testId="damaged-confirm"
        pending={damagedFor && busyId === `${damagedFor.id}`}
        onConfirm={() => {
          const target = damagedFor;
          setDamagedFor(null);
          runAction(target, () => api.post(`/admin/bookings/${target.id}/confirm-return?damaged=true`), "Diterima dengan catatan rusak");
        }}
      />

      <ConfirmDialog
        open={!!forceFor}
        onOpenChange={(o) => !o && setForceFor(null)}
        title={`Tandai kembali ${forceFor?.code ?? ""}`}
        description="Dipakai kalau alat sudah fisik kembali tapi peminjam belum sempat mengajukan. Tercatat atas nama admin."
        confirmLabel="Tandai kembali"
        danger={false}
        testId="force-return-confirm"
        pending={forceFor && busyId === `${forceFor.id}`}
        onConfirm={() => {
          const target = forceFor;
          setForceFor(null);
          runAction(target, () => api.post(`/admin/bookings/${target.id}/confirm-return`), "Booking ditandai kembali");
        }}
      />
    </div>
  );
}

function QueueHeader({ total, refreshing, onRefresh }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
      <h1 className="text-2xl font-semibold tracking-tight text-white">Perlu tindakan</h1>
      <p className="text-sm text-zinc-400">
        {total == null ? "Memuat antrean…" : total === 0 ? "Tidak ada yang menunggu." : `${total} booking menunggu keputusan admin, paling mendesak dulu.`}
      </p>
      {refreshing ? <Refreshing /> : onRefresh && (
        <button onClick={onRefresh} className="ml-auto text-xs text-zinc-500 underline-offset-4 hover:text-zinc-300 hover:underline">
          Muat ulang
        </button>
      )}
    </div>
  );
}

function PhotoThumbs({ photos, label }) {
  return (
    <span className="flex flex-wrap gap-1.5">
      {photos.map((p, i) => (
        <ProtectedImage
          key={`${p}-${i}`}
          src={p}
          alt={`${label} ${i + 1}`}
          className="h-14 w-14 rounded-lg border border-white/10 object-cover"
        />
      ))}
    </span>
  );
}

function RowMeta({ tab, booking: b }) {
  if (tab === "pending") return <>{fmtShort(b.start_time)} - {fmtShort(b.end_time)} &middot; {b.purpose}</>;
  if (tab === "returns") return <>Kembali: {fmtShort(b.end_time)}{b.return_condition ? ` - Kondisi: ${fmtCondition(b.return_condition)}` : ""}</>;
  return <>Jatuh tempo {fmtShort(b.end_time)}</>;
}

function DetailPanel({ booking: b, tab, busyId, onClose, onApprove, onReject, onAcceptReturn, onDamaged, onExtend, onForce }) {
  const selectedId = b?.id;
  useEffect(() => {
    if (selectedId && window.innerWidth < 1440) {
      document.getElementById("queue-detail")?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [selectedId]);

  if (!b) {
    return (
      <aside aria-label="Rincian booking" className="hidden rounded-2xl border border-dashed border-white/15 p-8 text-center min-[1440px]:block">
        <p className="text-sm text-zinc-500">Pilih booking di daftar untuk melihat rincian dan bertindak.</p>
      </aside>
    );
  }

  const busy = busyId === `${b.id}`;
  return (
    <aside
      id="queue-detail"
      aria-label="Rincian booking"
      className="overflow-hidden rounded-2xl border border-white/10 bg-zinc-900 min-[1440px]:sticky min-[1440px]:top-20"
    >
      <div className="border-b border-white/10 px-5 py-4">
        <div className="flex items-center justify-between gap-2">
          <p className="font-mono text-xs text-zinc-500">{b.code}</p>
          <button onClick={onClose} className="rounded-full px-2 py-1 text-xs text-zinc-500 hover:bg-white/10 hover:text-white min-h-[44px] min-[1440px]:hidden">
            Tutup rincian
          </button>
        </div>
        <h2 className="mt-1 text-lg font-semibold text-white">{b.user_name}</h2>
        <p className="mt-0.5 text-sm text-zinc-400">{b.purpose}</p>
      </div>

      <dl className="grid grid-cols-[128px_minmax(0,1fr)] gap-x-3 gap-y-2 px-5 py-4 text-sm min-[1440px]:grid-cols-[140px_minmax(0,1fr)]">
        <dt className="text-zinc-500">Total</dt><dd className="tabular-nums text-zinc-200">{b.total_qty ?? (b.lines || []).reduce((n, l) => n + (l.qty || 0), 0)} pcs</dd>
        <dt className="text-zinc-500">Jadwal ambil</dt><dd className="text-zinc-200">{fmtLong(b.start_time)}</dd>
        <dt className="text-zinc-500">Jadwal kembali</dt><dd className="text-zinc-200">{fmtLong(b.end_time)}</dd>
        {tab === "returns" && b.return_condition && (
          <><dt className="text-zinc-500">Kondisi lapor</dt><dd className="text-zinc-200">{fmtCondition(b.return_condition)}</dd></>
        )}
        {tab === "returns" && b.return_notes && (
          <><dt className="text-zinc-500">Catatan</dt><dd className="break-words text-zinc-200">{b.return_notes}</dd></>
        )}
        {Array.isArray(b.pickup_photos) && b.pickup_photos.length > 0 && (
          <><dt className="text-zinc-500">Foto ambil</dt><dd><PhotoThumbs photos={b.pickup_photos} label="Foto pengambilan" /></dd></>
        )}
        {Array.isArray(b.return_photos) && b.return_photos.length > 0 && (
          <><dt className="text-zinc-500">Foto kembali</dt><dd><PhotoThumbs photos={b.return_photos} label="Foto pengembalian" /></dd></>
        )}
      </dl>

      <table className="w-full border-t border-white/10 text-sm" aria-label="Rincian alat">
        <tbody>
          {(b.lines || []).map((l) => (
            <tr key={l.item_id || l.item_code} className="border-b border-dashed border-white/10 last:border-0">
              <td className="px-5 py-2.5 text-zinc-200">{l.name}</td>
              <td className="whitespace-nowrap px-5 py-2.5 text-right tabular-nums text-zinc-400">{l.qty} pcs</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="flex flex-col gap-2 px-5 py-4">
        {tab === "pending" && (
          <>
            <Button data-testid={`approve-button-${b.code}`} disabled={busy} onClick={() => onApprove(b)}
              className="h-12 rounded-full bg-[#0069DB] font-semibold text-white hover:bg-[#0058B6]">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Setujui"}
            </Button>
            <Button data-testid={`reject-button-${b.code}`} variant="outline" disabled={busy} onClick={() => onReject(b)}
              className="h-12 rounded-full border-rose-500/40 bg-transparent text-rose-300 hover:bg-rose-500/10">
              Tolak dengan alasan
            </Button>
            <p className="text-xs text-zinc-500">Persetujuan dibuat setelah rincian diperiksa. Kode akses dibuat otomatis.</p>
          </>
        )}
        {tab === "returns" && (
          <>
            <Button data-testid={`accept-return-button-${b.code}`} disabled={busy} onClick={() => onAcceptReturn(b)}
              className="h-12 rounded-full bg-emerald-500 font-semibold text-emerald-950 hover:bg-emerald-400">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Konfirmasi terima"}
            </Button>
            <Button data-testid={`damaged-return-button-${b.code}`} variant="outline" disabled={busy} onClick={() => onDamaged(b)}
              className="h-12 rounded-full border-amber-500/40 bg-transparent text-amber-300 hover:bg-amber-500/10">
              Terima dengan catatan rusak
            </Button>
            <p className="text-xs text-zinc-500">Periksa fisik alat dan foto sebelum konfirmasi.</p>
          </>
        )}
        {tab === "overdue" && (
          <>
            <Button data-testid={`extend-button-${b.code}`} variant="outline" disabled={busy} onClick={() => onExtend(b)}
              className="h-12 rounded-full border-white/15 bg-transparent text-zinc-200 hover:bg-white/10">
              Perpanjang
            </Button>
            <Button data-testid={`force-return-button-${b.code}`} disabled={busy} onClick={() => onForce(b)}
              className="h-12 rounded-full bg-white font-semibold text-black hover:bg-zinc-200">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Tandai kembali"}
            </Button>
            <p className="text-xs text-zinc-500">Perpanjangan menampilkan jam kembali yang baru ke peminjam.</p>
          </>
        )}
      </div>
    </aside>
  );
}
